/**
 * Route → Vale Help Centre deep-link mapping (LF-5290).
 *
 * Matched in order against the current pathname, first match wins, and the
 * registry orders the generic matter fallback after every specific matter-tab
 * route. Any pathname that matches nothing falls back to the Help Centre home.
 */

import {
  GUIDANCE_AREAS,
  HELP_CENTRE_ORIGIN,
  helpDestinationUrl,
  listHelpRoutes
} from '@/lib/guidance/areas';

/*
  **The mappings live in the guidance-area registry now** (VALE-1867), beside
  the banner and tours of the same product area, so "what guidance does
  Discovery have?" is one lookup rather than three files. This module keeps the
  Help button's API and its catch-all constants; add or repoint a mapping in
  `lib/guidance/areas.ts`, and expect `__tests__/guidance-area-registry.test.ts`'s
  per-route snapshot to show the change.
*/

export const HELP_CENTRE_HOME_URL = HELP_CENTRE_ORIGIN;

/**
 * The catch-all destination for a matter tab with no dedicated article: the
 * Estate-Administration "administering a matter" collection.
 *
 * Named because landing here is **matter-type-specific**: the content assumes a
 * deceased estate, so a tab that falls through to it is misleading on a
 * non-estate matter and must hide its Help button (LF-5894 did exactly that for
 * Overview). Tabs with their own article are unaffected, since their content is
 * matter-type-neutral, which is why Documents / Contacts / Communications keep
 * Help on a non-estate matter (LF-5910). `help-centre.test.ts` walks the
 * non-estate tab-set against this constant so a newly-added tab that falls
 * through here fails the suite rather than shipping the same trap again.
 */
export const GENERIC_MATTER_FALLBACK_URL = (() => {
  const fallback = GUIDANCE_AREAS.flatMap((area) => area.help).find(
    (route) => route.key === 'matter-generic'
  );
  if (!fallback) {
    throw new Error(
      'The guidance-area registry lost its matter-generic mapping.'
    );
  }
  return helpDestinationUrl(fallback.destination);
})();

const AREA_BY_ROUTE = new Map(
  GUIDANCE_AREAS.flatMap((area) => area.help.map((route) => [route, area.id]))
);

const HELP_CENTRE_MAPPINGS = listHelpRoutes().map((route) => ({
  key: route.key,
  label: route.label,
  areaId: AREA_BY_ROUTE.get(route),
  matches: route.matches,
  url: helpDestinationUrl(route.destination)
}));

/** The name reported for a pathname that matches no mapping at all. */
export const HELP_CENTRE_HOME_KEY = 'help-centre-home';

/**
 * The Help button's label where it goes to the Help Centre home: there is no
 * page-specific guide to name, so it says only what it is.
 */
export const HELP_CENTRE_HOME_LABEL = 'Help';

/** Where the Help button goes, and the name of the mapping that decided it. */
export interface HelpCentreTarget {
  /** The matched mapping's `key`, or {@link HELP_CENTRE_HOME_KEY}. */
  key: string;
  url: string;
  /** The mapping's label, or {@link HELP_CENTRE_HOME_LABEL}. */
  label: string;
  /** The guidance area owning the matched mapping; none for the home fallback. */
  areaId?: string;
}

/**
 * Resolves the Help Centre destination for the given pathname, and names the
 * mapping that decided it.
 *
 * **Bind here rather than re-running the regexes.** The button needs both the
 * destination and a reportable name for the page (VALE-1790), and this list is
 * the only thing that knows which pathname segments are ids, so a caller that
 * derived its own route label would be a second, drifting definition of exactly
 * that. {@link getHelpCentreUrl} delegates here.
 */
export function getHelpCentreTarget(pathname: string): HelpCentreTarget {
  const match = HELP_CENTRE_MAPPINGS.find((m) => m.matches.test(pathname));
  if (!match) {
    return {
      key: HELP_CENTRE_HOME_KEY,
      url: HELP_CENTRE_HOME_URL,
      label: HELP_CENTRE_HOME_LABEL
    };
  }
  return {
    key: match.key,
    url: match.url,
    label: match.label,
    ...(match.areaId ? { areaId: match.areaId } : {})
  };
}

/** Resolves the Help Centre destination for the given pathname. */
export function getHelpCentreUrl(pathname: string): string {
  return getHelpCentreTarget(pathname).url;
}
